import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightDots,
  faCircleArrowLeft,
  faCirclePlus,
} from "@fortawesome/free-solid-svg-icons";
import {
  Button,
  Col,
  Form,
  FormCheck,
  InputGroup,
  OverlayTrigger,
  Popover,
  Row,
} from "react-bootstrap";
import HelpButton, { HelpContent } from "../HelpContent/HelpButton";
import MyIcon from "../Common/MyIcon";
import UsersIcon from "../../assets/icons/Icon_Library/application icons/settings/users_roles.svg2";
import {
  useGetUserRolesEditDataQuery,
  useGetUsersSettingsFilterDataQuery,
  useLazyGetUserRolesCreateDataQuery,
  useLazyGetUserRolesEditDataQuery,
  useStoreUserRolesDataMutation,
  useUpdateUserRolesDataMutation,
} from "../store/slices/settingsSlice";
import { Link, useNavigate, useParams } from "react-router-dom";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import UpdateIcon from "../../assets/icons/Icon_Library/application icons/general/save.svg2";
import CreateIcon from "../../assets/icons/Icon_Library/application icons/settings/users_roles.svg2";

import JsonCollapse from "../Builder/Components/JsonCollapse";
import { toast } from "react-toastify";
import { components } from "react-select";
import Subheading from "../Common/Subheading";

const exampleData = {
  data: {
    canEdit: true,
    permissions: {
      system_settings: {
        list: {
          view_system_setting: {
            title: "View",
            type: "view",
            enum: "view_system_setting",
          },
          edit_system_setting: {
            title: "Edit",
            type: "edit",
            enum: "edit_system_setting",
          },
          admin_sensitive_data: {
            title: "Admin Sensitive Data",
            type: "admin",
            enum: "admin_sensitive_data",
          },
        },
        name: "System settings",
        help_tips_id: "132",
        products: [
          "alert",
          "cast",
          "flow",
          "mail",
          "api",
          "engage",
          "inform",
          "logic",
        ],
      },
      organisation_settings: {
        list: {
          view_organisation_setting: {
            title: "View",
            type: "view",
            enum: "view_organisation_setting",
          },
          edit_organisation_setting: {
            title: "Edit",
            type: "edit",
            enum: "edit_organisation_setting",
          },
        },
        name: "Organisation settings",
        help_tips_id: "133",
        products: [
          "alert",
          "cast",
          "flow",
          "mail",
          "api",
          "engage",
          "inform",
          "logic",
        ],
      },
      users: {
        list: {
          view_user: { title: "View", type: "view", enum: "view_user" },
          edit_user: { title: "Edit", type: "edit", enum: "edit_user" },
        },
        name: "Manage Users",
        help_tips_id: "134",
        products: [
          "alert",
          "cast",
          "flow",
          "mail",
          "api",
          "engage",
          "inform",
          "logic",
        ],
      },
      permissions: {
        list: {
          view_permissions: {
            title: "View",
            type: "view",
            enum: "view_permissions",
          },
          edit_permissions: {
            title: "Edit",
            type: "edit",
            enum: "edit_permissions",
          },
        },
        name: "Manage User Roles",
        help_tips_id: "135",
        products: [
          "alert",
          "cast",
          "flow",
          "mail",
          "api",
          "engage",
          "inform",
          "logic",
        ],
      },
      templates: {
        list: {
          view_template: { title: "View", type: "view", enum: "view_template" },
          use_template: { title: "Use", type: "use", enum: "use_template" },
          edit_template: { title: "Edit", type: "edit", enum: "edit_template" },
        },
        name: "Templates",
        help_tips_id: "136",
        products: ["cast", "engage", "inform", "logic"],
      },
      contacts: {
        list: {
          view_contact: { title: "View", type: "view", enum: "view_contact" },
          sensitive_data: {
            title: "View Sensitive Data",
            type: "sensitive",
            enum: "sensitive_data",
          },
          edit_contact: { title: "Edit", type: "edit", enum: "edit_contact" },
        },
        name: "Contacts",
        help_tips_id: "137",
        products: ["alert", "cast", "flow", "engage", "inform", "logic"],
      },
      groups: {
        list: {
          view_group: { title: "View", type: "view", enum: "view_group" },
          edit_group: { title: "Edit", type: "edit", enum: "edit_group" },
        },
        name: "Groups",
        help_tips_id: "138",
        products: ["alert", "cast", "flow", "engage", "inform", "logic"],
      },
      reporting: {
        list: {
          view_reporting: {
            title: "View",
            type: "view",
            enum: "view_reporting",
          },
          export_reporting: {
            title: "Export",
            type: "export",
            enum: "export_reporting",
          },
        },
        name: "Reporting",
        help_tips_id: "139",
        products: ["cast", "mail", "alert", "api", "engage", "inform", "logic"],
      },
      message_hub: {
        list: {
          view_message_hub: {
            title: "View",
            type: "view",
            enum: "view_message_hub",
          },
        },
        name: "Message Hub",
        help_tips_id: "140",
        products: ["cast"],
      },
      help: {
        list: { edit_help: { title: "Edit", type: "edit", enum: "edit_help" } },
        name: "Help",
        help_tips_id: "141",
        products: ["alert", "cast", "flow", "mail", "api", "engage", "inform"],
      },
      incident_hub: {
        list: {
          use_incident_hub: {
            title: "Use",
            type: "use",
            enum: "use_incident_hub",
          },
        },
        name: "Incident Hub",
        help_tips_id: "142",
        products: ["cast", "inform"],
      },
      inbound_campaign: {
        list: {
          view_inbound_campaign: {
            title: "View",
            type: "view",
            enum: "view_inbound_campaign",
          },
          use_inbound_campaign: {
            title: "Use",
            type: "use",
            enum: "use_inbound_campaign",
          },
        },
        name: "Inbound Campaign",
        help_tips_id: "143",
        products: ["cast", "mail", "engage", "logic"],
      },
      outbound_broadcast: {
        list: {
          use_outbound_broadcast: {
            title: "Use",
            type: "use",
            enum: "use_outbound_broadcast",
          },
        },
        name: "Build Outbound Broadcast",
        help_tips_id: "146",
        products: ["cast", "logic"],
      },
      bypass_departments: {
        list: {
          bypass_message_hub: {
            title: "Bypass Message Hub",
            type: "view",
            enum: "bypass_message_hub",
          },
          bypass_full: {
            title: "Bypass all",
            type: "use",
            enum: "bypass_full",
          },
        },
        name: "Departments",
        help_tips_id: "152",
        products: ["cast"],
      },
      projects: {
        list: {
          view_project: { title: "View", type: "view", enum: "view_project" },
          use_project: { title: "Use", type: "use", enum: "use_project" },
          edit_project: { title: "Edit", type: "edit", enum: "edit_project" },
          edit_production: {
            title: "Production Ready",
            type: "edit-live",
            enum: "edit_production",
          },
        },
        name: "Projects",
        help_tips_id: "287",
        products: ["alert", "logic"],
      },
      api: {
        list: {
          view_api: { title: "View", type: "view", enum: "view_api" },
          edit_api: { title: "Edit", type: "edit", enum: "edit_api" },
        },
        name: "Integrations/API",
        help_tips_id: "232",
        products: ["alert", "cast", "api", "inform", "logic"],
      },
      triage: {
        list: { use_triage: { title: "Use", type: "use", enum: "use_triage" } },
        name: "Triage",
        help_tips_id: "286",
        products: ["mail", "cast", "engage"],
      },
      hierarchy: {
        list: {
          access_subaccounts: {
            title: "Access subaccounts",
            type: "view",
            enum: "access_subaccounts",
          },
        },
        name: "Subaccounts",
        help_tips_id: "0",
        products: ["alert", "cast", "flow", "mail", "api", "engage", "inform"],
      },
      billing: {
        list: {
          access_billing: {
            title: "Access billing",
            type: "view",
            enum: "access_billing",
          },
        },
        name: "Billing",
        help_tips_id: "0",
        products: [
          "alert",
          "cast",
          "flow",
          "mail",
          "api",
          "engage",
          "inform",
          "logic",
        ],
      },
      expert: {
        list: {
          expert_logic_view: {
            title: "read only",
            type: "view",
            enum: "expert_logic_view",
          },
          expert_logic_edit: {
            title: "edit",
            type: "edit",
            enum: "expert_logic_edit",
          },
        },
        name: "Logic Expert Builder",
        help_tips_id: "0",
        products: ["logic"],
      },
    },
    UserRole: {
      id: 45,
      customer_id: 1,
      name: "User",
      description:
        "Allows message sending and editing of contacts and groups and templates and only a view of high level functions e.g. System Settings, Organisation Settings",
      type: "standalone",
      user_permission_roles: [
        {
          user_role_id: 45,
          permission_enum: "view_system_setting",
          replication_id: 1,
        },
        {
          user_role_id: 45,
          permission_enum: "edit_system_setting",
          replication_id: 2,
        },
        {
          user_role_id: 45,
          permission_enum: "view_permissions",
          replication_id: 3,
        },
        {
          user_role_id: 45,
          permission_enum: "edit_permissions",
          replication_id: 4,
        },
      ],
      clients: [
        {
          id: 1,
          user_subscriber_id: 1,
          client_name: "Default boomerang client 2025-03-21 08:53:26",
          active: "Y",
          dpt_active: "N",
          in_use: "Y",
          server_url: "default",
          username: "qN9sw07t93Po",
          license_key: "0K0D7-I9A31-87QJ1-8H697-85U3N-B4EZF",
          account_id: 11,
          customer_id: 1,
          product_type: "cast",
          is_trial: "N",
          trial_date_end: null,
          credits: 0,
          pay_type: "postpaid",
          trial_log_id: null,
          email_from: null,
          email_subject: null,
          report_subject: null,
          service_name: null,
          www_domain: null,
          email_domain: "",
          email_logo: null,
          email_sig: null,
          excel_file: null,
          excel_header: null,
          notify_trigger: 0,
          confirm_trigger: 0,
          secondary_trigger: 0,
          default_override: 0,
          theme: 1,
          country: 44,
          language: 1,
          dateformat: 1,
          currency: 1,
          keeplogs: 30,
          wizard: "N",
          guide: "Y",
          timeout: 2,
          pass_renew: 30,
          login_attempts: 5,
          secure_data: "N",
          secure_data_override: "Y",
          mx_record_set: "Y",
          country_id: 79,
          request_domain: "N",
          is_connector_trial: 0,
          timezone: "Europe/London",
          do_not_disable_gw: 0,
          workflows_client_id: 0,
          workflows_service: "",
          no_access: 0,
          gw_inbound_list_id: 0,
          pivot: { user_role_id: 45, client_id: 1 },
        },
      ],
      template: [
        {
          id: 30,
          customer_id: 0,
          name: "User",
          description:
            "Allows message sending and editing of contacts and groups and templates and only a view of high level functions e.g. System Settings, Organisation Settings",
          type: "standalone",
          pivot: { user_role_id: 45, user_role_tpl_id: 30 },
        },
      ],
    },
    templateRoleId: false,
  },
  selectedClientIds: [1],
  selectedPermissions: [
    "view_system_setting",
    "edit_system_setting",
    "view_permissions",
    "edit_permissions",
  ],
  selectedProductType: ["cast"],
  role_type: "standalone",
  show_update: true,
};

const Menu = ({ children, ...props }) => (
  <components.Menu {...props}>
    <div>The user will have access to selected product(s)</div>
    {children}
  </components.Menu>
);

const noProductWarningPopover = (
  <Popover id="popover-basic">
    <Popover.Header as="h3">Please Select</Popover.Header>
    <Popover.Body>
      You need to select <strong> a product</strong> to start.
    </Popover.Body>
  </Popover>
);

function UserRoleEditPage() {
  const navigate = useNavigate();
  const { roleID } = useParams();

  const [bd, setBd] = useState(); // backend data to be used in the form

  // data to be sent when updating/saving
  const [data, setData] = useState({
    role_name: "",
    role_id: "",
    description: "",
    data: [
      {
        type: "shared",
        list: [],
        client_ids: [],
      },
    ],
  });

  const [getData] = useLazyGetUserRolesEditDataQuery();
  const [getCreateData] = useLazyGetUserRolesCreateDataQuery();
  const { data: clients = [] } = useGetUsersSettingsFilterDataQuery();
  const [updateData] = useUpdateUserRolesDataMutation();
  const [createNew] = useStoreUserRolesDataMutation();

  useEffect(() => {
    (async () => {
      // stop if roleID is undefined
      if (!roleID) return;

      // set data for new role
      if (roleID === "new") {
        const { data: permissions } = await getCreateData();

        setBd({
          data: { UserRole: { clients }, permissions },
        });
        setData({
          role_name: "",
          role_id: "",
          description: "",
          data: [
            {
              type: "shared",
              list: [],
              client_ids: [],
            },
          ],
        });
        return;
      }

      // get role data from backend
      const { data: d } = await getData(roleID);
      setBd(d);
      setData({
        role_name: d.data.UserRole.name,
        role_id: d.data.UserRole.id,
        description: d.data.UserRole.description,
        data: [
          {
            type: d.role_type,
            list: d.selectedPermissions,
            client_ids: d.selectedClientIds,
          },
        ],
      });
    })();
  }, [roleID]);

  const selectedProducts = clients
    .filter((c) => data.data[0].client_ids.includes(c.id))
    .map((c) => c.product_type);

  return (
    data &&
    bd && (
      <>
        <div className="bg-purple-gradient-darker-reverse rounded-4 p-3 mb-4">
          <div className="d-flex justify-content-between">
            <h5 className=" d-flex align-items-center">
              <MyIcon Icon={UsersIcon} className="me-2" />
              <span className="ms-2">
                <span className="fw-bold me-2"> Manage User Roles:</span>
                <HelpButton helpKey="settings-users-manage-users-roles" />

                <div className="small text-secondary">
                  Roles manage security access by grouping permissions, allowing
                  users to be assigned appropriate system access.
                </div>
              </span>
            </h5>
            {/* <div className="d-flex align-items-center">
              <button
                type="submit"
                className="btn btn-colored-reverse rounded-pill fw-bold   "
              >
                <FontAwesomeIcon icon={faCirclePlus} className="me-2" />
                Add New Role
                <HelpButton helpKey="settings-manage-users-roles-add-new-role" />
              </button>
            </div> */}
          </div>
        </div>
        <div className="bg-white p-3">
          <div className="bg-purple-gradient-darker-reverse rounded-4 p-3 mb-4 ">
            <div className="py-2">
              <h5 className=" d-flex align-items-center">
                <MyIcon Icon={UsersIcon} className="me-2" />
                <span className="">
                  <span className="fw-bold me-2">Creating a new role </span>
                  <HelpButton helpKey="settings-users-creating-new-role" />
                  <div className="small text-secondary">
                    You can start creating a new role
                  </div>
                </span>
              </h5>
            </div>
            <Row className="align-items-start">
              <Col>
                <InputGroup>
                  <InputGroup.Text>Role Name</InputGroup.Text>
                  <Form.Control
                    placeholder="Give your role a name e.g.Administrator"
                    value={data.role_name}
                    onChange={(e) => {
                      setData((p) => ({
                        ...p,
                        role_name: e.target.value,
                      }));
                    }}
                  />
                </InputGroup>
              </Col>
              <Col>
                <Form.Control
                  placeholder="Please type role description"
                  as="textarea"
                  aria-label="With textarea"
                  value={data.description}
                  onChange={(e) => {
                    setData((p) => ({
                      ...p,
                      description: e.target.value,
                    }));
                  }}
                />
              </Col>
              <Col>
                <Form.Check
                  type="switch"
                  label={`${
                    data.data[0].type === "standalone"
                      ? "Create role in a single product"
                      : "Share role across products"
                  }`}
                  className="ps-0 fs-5"
                  checked={data.data[0].type === "standalone"}
                  disabled={data.data[0].client_ids.length > 1}
                  onChange={(e) => {
                    setData((p) => ({
                      ...p,
                      data: [
                        {
                          ...p.data[0],
                          type: e.target.checked ? "standalone" : "shared",
                        },
                      ],
                    }));
                  }}
                />
              </Col>
              <Col>
                <OverlayTrigger
                  show={!selectedProducts.length}
                  placement="top"
                  overlay={noProductWarningPopover}
                >
                  <div>
                    <Input
                      placeholder="Nothing Selected"
                      inputType="select"
                      isMulti={
                        data.data[0].type === "standalone" ? false : true
                      }
                      value={data.data[0].client_ids}
                      options={clients.map((c) => ({
                        value: c.id,
                        label: c.product_name,
                      }))}
                      formatOptionLabel={(o) => (
                        <div>
                          <span className="badge bg-secondary small me-2">
                            {clients.find((c) => c.id === o.value).product_type}
                          </span>
                          {o.label}
                        </div>
                      )}
                      components={{
                        Menu,
                      }}
                      onChange={(values) => {
                        setData((p) => ({
                          ...p,
                          data: [
                            {
                              ...p.data[0],
                              client_ids: values,
                            },
                          ],
                        }));
                      }}
                    />
                  </div>
                </OverlayTrigger>
              </Col>
            </Row>
          </div>
          {selectedProducts.length ? (
            <>
              <div className="fw-bold fs-5 my-3 ">
                The role you are creating will be specific to the product
                selected.
              </div>
              {data.data[0].type === "shared" && (
                <div className="fw-bold fs-6 my-3 ">
                  The following permissions are common to all of the selected
                  products
                </div>
              )}
              <Row className="bg-white g-3">
                {Object.keys(bd?.data.permissions ?? {}).map((k) => {
                  const permission = bd?.data.permissions[k];
                  const listKeys = Object.keys(permission.list);

                  // do not show the permission if selected products is missing in permission products
                  if (
                    selectedProducts.some(
                      (sp) => !permission.products.includes(sp)
                    )
                  ) {
                    return null;
                  }
                  return (
                    <Col md={4} key={k}>
                      <div className="bg-purple-gradient-darker-reverse rounded-4 p-3">
                        <div className="d-flex justify-content-between align-items-center ">
                          <div>
                            <h5 className="fw-bold">
                              {permission.name}
                              <HelpButton helpKey={`user-permission-${k}`} />
                            </h5>
                          </div>
                          <div>
                            {permission.products
                              .filter((rp) => selectedProducts.includes(rp))
                              .map((rp) => (
                                <span className="badge bg-secondary me-2">
                                  {rp}
                                </span>
                              ))}
                          </div>
                        </div>
                        <div
                          style={{ height: "150px", overflow: "auto" }}
                          className="text-secondary border-bottom pb-2 mb-2"
                        >
                          {permission.description && (
                            <p className="mb-2">{permission.description}</p>
                          )}
                          <HelpContent helpKey={`user-permission-${k}`} />
                        </div>

                        <div className="d-flex my-2">
                          {listKeys.map((lk, lki) => (
                            <div>
                              <FormCheck
                                className="p-0 me-4"
                                id={permission.list[lk].enum}
                                label={permission.list[lk].title}
                                checked={data.data[0].list.includes(
                                  permission.list[lk].enum
                                )}
                                disabled={data.data[0].list.includes(
                                  Object.values(permission.list)[lki + 1]?.enum
                                )}
                                onChange={() => {
                                  setData((p) => {
                                    let uprs = [...data.data[0].list];
                                    if (
                                      uprs.includes(permission.list[lk].enum)
                                    ) {
                                      uprs = uprs.filter(
                                        (ur) => ur !== permission.list[lk].enum
                                      );
                                    } else {
                                      uprs.push(
                                        ...Object.values(permission.list)
                                          .slice(0, lki + 1)
                                          .map((i) => i.enum)
                                      );
                                    }
                                    return {
                                      ...p,
                                      data: [
                                        {
                                          ...p.data[0],
                                          list: uprs,
                                        },
                                      ],
                                    };
                                  });
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </Col>
                  );
                })}
                <div className="d-flex align-items-center justify-content-end mb-4 gap-3">
                  <div className="d-flex align-items-center">
                    <Link
                      to={`/settings/users`}
                      type="button"
                      className="btn btn-colored-reverse rounded-pill fw-bold px-4  "
                    >
                      <FontAwesomeIcon
                        icon={faCircleArrowLeft}
                        className="me-2"
                      />
                      Back
                    </Link>
                  </div>
                  <div className="d-flex align-items-center">
                    <button
                      onClick={async () => {
                        if (roleID === "new") {
                          const res = await createNew(data);
                          if (res.data) {
                            navigate("/settings/users");
                            toast.success("Saved Successfully ");
                          } else {
                            toast.error(
                              `${res.error.data.message}\n${Object.values(
                                res.error.data.errors || {}
                              ).join("\n")}`
                            );
                          }
                        } else {
                          const res = await updateData(data);
                          if (res.data) {
                            toast.success("Updated Successfully ");
                          } else {
                            toast.error(
                              `${res.error.data.message}\n${Object.values(
                                res.error.data.errors || {}
                              ).join("\n")}`
                            );
                          }
                        }
                      }}
                      type="button"
                      className="btn btn-colored-reverse rounded-pill fw-bold  px-4 "
                    >
                      <UpdateIcon width={15} className="text-white me-2" />
                      {roleID === "new" ? "Save" : "Update"}
                    </button>
                  </div>
                </div>
              </Row>
            </>
          ) : (
            <div className="">
              {/* You need to select a product to start
              <FontAwesomeIcon icon={faArrowUpRightDots} width={50} /> */}
            </div>
          )}
          <JsonCollapse json={data} />
        </div>
      </>
    )
  );
}

export default UserRoleEditPage;
